import type { DataTableColumnConfig } from "building-types/dataTables/dataTable";
import type { KeysOfUnion, Modify } from "common/utils/types";
import type { CeilingKeyAttributes, CeilingKeyMeasures } from "./builtin/CeilingKAM";
import type { DoorKeyAttributes, DoorKeyMeasures } from "./builtin/DoorKAM";
import type { ElectricalKeyAttributes, ElectricalKeyMeasures } from "./builtin/ElectricalKAM";
import type { FlooringKeyAttributes, FlooringKeyMeasures } from "./builtin/FlooringKAM";
import type { FootingFoundationKeyAttributes, FootingFoundationKeyMeasures } from "./builtin/FootingFoundationKAM";
import type { FramingKeyAttributes, FramingKeyMeasures } from "./builtin/FramingKAM";
import type { PropKeyAttributes, PropKeyMeasures } from "./builtin/PropKAM";
import type { RoofKeyAttributes, RoofKeyMeasures } from "./builtin/RoofKAM";
import type { LinearRoofKeyAttributes, LinearRoofKeyMeasures } from "./builtin/RoofRidgeKAM";
import type { SlabFoundationKeyAttributes, SlabFoundationKeyMeasures } from "./builtin/SlabFoundationKAM";
import type { WallKeyAttributes, WallKeyMeasures } from "./builtin/WallKAM";
import type { WindowKeyAttributes, WindowKeyMeasures } from "./builtin/WindowKAM";
import type { DataSequenceUnit } from "./dataSequenceUnitTypes";
import type { KeyAttributeOptions, KeyMeasureOptions } from "./measures";
import type { SystemDataSequenceEntityType } from "./systemDataSequenceTypes";

type KeyAttributeTypes =
  | WindowKeyAttributes
  | DoorKeyAttributes
  | CeilingKeyAttributes
  | ElectricalKeyAttributes
  | FlooringKeyAttributes
  | FootingFoundationKeyAttributes
  | SlabFoundationKeyAttributes
  | FramingKeyAttributes
  | PropKeyAttributes
  | RoofKeyAttributes
  | LinearRoofKeyAttributes
  | WallKeyAttributes;

type KeyMeasureTypes =
  | WindowKeyMeasures
  | DoorKeyMeasures
  | CeilingKeyMeasures
  | ElectricalKeyMeasures
  | FlooringKeyMeasures
  | FootingFoundationKeyMeasures
  | SlabFoundationKeyMeasures
  | FramingKeyMeasures
  | PropKeyMeasures
  | RoofKeyMeasures
  | LinearRoofKeyMeasures
  | WallKeyMeasures;

export type KeyAttributeTypesKeys = KeysOfUnion<KeyAttributeTypes>;
export type KeyMeasureTypesKeys = KeysOfUnion<KeyMeasureTypes>;

export const dataSequenceUnitConversionFunctions: {
  [Property in DataSequenceUnit]: {
    [Property in DataSequenceUnit]?: (value: number) => number;
  };
} = {
  "CU FT": {
    "CU IN": (value) => value * 1728,
    "CU FT": (value) => value,
  },
  "CU IN": {
    "CU IN": (value) => value,
    "CU FT": (value) => value / 1728,
  },
  FEET: {
    INCHES: (value) => value * 12,
    FEET: (value) => value,
  },
  INCHES: {
    INCHES: (value) => value,
    FEET: (value) => value / 12,
  },
  QUANTITY: {
    QUANTITY: (value) => value,
  },
  "SQ FT": {
    "SQ IN": (value) => value * 144,
    "SQ FT": (value) => value,
  },
  "SQ IN": {
    "SQ IN": (value) => value,
    "SQ FT": (value) => value / 144,
  },
};

interface KeyAttributeVisualizationOptions {
  index: number;
  isHidden: boolean;
  displayName: string;
  includeFilter: string;
  excludeFilter: string;
}

// rollup
interface KeyMeasureVisualizationOptions {
  index: number;
  isHidden: boolean;
  displayName: string;
}

export type KeyAttributeSettingOptions = Modify<
  KeyAttributeOptions,
  { definesEntity: boolean; unit?: DataSequenceUnit }
> &
  KeyAttributeVisualizationOptions;

export type KeyAttributeSettings = {
  [key in KeyAttributeTypesKeys]?: KeyAttributeSettingOptions;
};

export type ColumnHeaderOptions =
  | KeyAttributeSettingOptions
  | KeyMeasureSettingOptions
  | (Required<Pick<DataTableColumnConfig, "displayName">> & DataTableColumnConfig);

export type KeyMeasureSettingOptions = Modify<KeyMeasureOptions, { isAggregated: boolean; unit: DataSequenceUnit }> &
  KeyMeasureVisualizationOptions;

export type KeyMeasureSettings = {
  [key in KeyMeasureTypesKeys]?: KeyMeasureSettingOptions;
};

export interface BuiltInDataTableSettings {
  readonly type: SystemDataSequenceEntityType;
  readonly scheduleName: string;
  readonly attributes: KeyAttributeSettings;
  readonly measures: KeyMeasureSettings;
  readonly footnote: string;
  readonly forceColumnNameUppercase: boolean;
  readonly isActive: boolean;
}

export type BuiltInDataSequenceSettingsTable = {
  [K in SystemDataSequenceEntityType]?: BuiltInDataTableSettings[];
};
